/**
 * talents-all.ts — 统一聚合入口
 * 将所有分批数据文件合并，并应用 name overrides，最终导出 talents 数组。
 * 消费方只需：import { talents } from '../../mocks/talents-all'
 *
 * 按类别分组导出：import { talentsByCategory } from '../../mocks/talents-all'
 */
import { talentsBusiness } from './talents-business';
import { talentsDev } from './talents-dev';
import { talentsAiAgent } from './talents-ai-agent';
import { talentsDesignBasic } from './talents-design-basic';
import { talentsDesignAdvanced } from './talents-design-advanced';
import { talentsLegalHr } from './talents-legal-hr';
import { talentsProductHome } from './talents-product-home';
import { talentsSocial } from './talents-social';
// ─── 合并批次文件（减少 mocks 目录文件数）────────────────────────────────────
import { talentsBatch2527 } from './talents-batch25-27';   // batch25 + batch27
import { talentsBatch2832 } from './talents-batch28-32';   // batch28 + batch32
// ─── 独立批次文件 ──────────────────────────────────────────────────────────────
import { talentsBatch29 } from './talents-batch29';
import { talentsBatch30 } from './talents-batch30';
import { talentsBatch31 } from './talents-batch31';
import { talentsBatch33 } from './talents-batch33';
import { talentsHrUpdated } from './talents-hr-updated';
import { talentsDataOpsUpdated } from './talents-data-ops-updated';
import { talentsLegalUpdated } from './talents-legal-updated';
import { applyNameOverrides } from './talent-name-overrides';
import { roleCategories, getRoleCategory } from './roleCategories';
import { talentCategoryOverrides } from './talent-category-overrides';

/**
 * 这些 ID 在原 talents.ts 中存在，但消费方一直通过 filter 过滤掉，
 * 因为它们的更新版本已在 talentsHrUpdated / talentsLegalUpdated 等文件中提供。
 */
const EXCLUDED_BASE_IDS = new Set([48, 49, 50, 51, 52, 53, 164]);

const baseList = [
  ...talentsBusiness,
  ...talentsDev,
  ...talentsAiAgent,
  ...talentsDesignBasic,
  ...talentsDesignAdvanced,
  ...talentsLegalHr,
  ...talentsProductHome,
  ...talentsSocial,
].filter((t: any) => !EXCLUDED_BASE_IDS.has(t.id));

const rawTalents = [
  ...baseList,
  ...talentsBatch2527,
  ...talentsBatch2832,
  ...talentsBatch29,
  ...talentsBatch30,
  ...talentsBatch31,
  ...talentsBatch33,
  ...talentsHrUpdated,
  ...talentsDataOpsUpdated,
  ...talentsLegalUpdated,
];

// 去重（以 id 为 key，保留第一次出现的）
const seenIds = new Set<number>();
const dedupedTalents = rawTalents.filter((t: any) => {
  if (seenIds.has(t.id)) return false;
  seenIds.add(t.id);
  return true;
});

/** 全量人才列表（已去重 + name override + category override） */
export const talents: any[] = applyNameOverrides(dedupedTalents).map((t: any) => {
  const catOverride = talentCategoryOverrides[t.id];
  if (catOverride) return { ...t, category: catOverride };
  return t;
});

// ─── 按角色类别分组导出 ────────────────────────────────────────────────────────
// 方便业务按类别快速取数据，如：
//   import { talentsByCategory } from '../../mocks/talents-all'
//   const designers = talentsByCategory['创意设计类']
const _grouped: Record<string, any[]> = {};
Object.keys(roleCategories).forEach(cat => { _grouped[cat] = []; });

talents.forEach(t => {
  const cat = getRoleCategory(t.role);
  if (!_grouped[cat]) _grouped[cat] = [];
  _grouped[cat].push(t);
});

/** 人才列表按岗位类别分组，key 为分类名称 */
export const talentsByCategory: Record<string, any[]> = _grouped;

/** 快速获取某分类下的人才列表（找不到返回空数组） */
export function getTalentsByCategory(category: string): any[] {
  return _grouped[category] ?? [];
}

/** 所有已有人才的分类名列表（按 roleCategories 顺序） */
export const availableCategories: string[] = Object.keys(roleCategories).filter(
  cat => (_grouped[cat]?.length ?? 0) > 0,
);
